import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { BASE_URL } from "../../../../config/api";

export default function KeysForm({ badge = null, onSuccess, onClose }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    key_id: "",
    key_secret: "",
  });
  const [errors, setErrors] = useState({});

  // Pre-fill form for update
  useEffect(() => {
    if (badge) {
      setForm({
        key_id: badge.key_id || "",
        key_secret: badge.key_secret || "",
      });
    }
  }, [badge]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validation
  const validate = () => {
    let newErrors = {};
    if (!form.key_id.trim()) newErrors.key_id = "Key ID is required";
    if (!form.key_secret.trim()) newErrors.key_secret = "Key Secret is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      let response;

      if (badge) {
        // Update key (both key_id & key_secret)
        response = await fetch(
          `${BASE_URL}payment/keys/${badge.id}`,
          {
            method: "PUT", // or PUT if API requires
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              key_id: form.key_id,
              key_secret: form.key_secret,
            }),
          }
        );
      } else {
        // Create key
        response = await fetch(`${BASE_URL}payment/keys`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            key_id: form.key_id,
            key_secret: form.key_secret,
          }),
        });
      }

      const data = await response.json();
      if (!response.ok) {
        toast.error(data?.message || "Failed to save key");
        return;
      }

      toast.success(`Key ${badge ? "updated" : "created"} successfully!`);
      onSuccess && onSuccess(data);
      onClose && onClose();
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-blue-50 shadow-sm p-4 w-full md:w-[400px] relative">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-slate-500 hover:text-slate-700"
      >
        <X size={20} />
      </button>

      <h2 className="font-semibold text-slate-900 text-sm md:text-base mb-3">
        {badge ? "Update Key" : "Create Key"}
      </h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 text-xs md:text-sm">
        {/* Key ID field is now editable for both create and update */}
        <div className="flex flex-col gap-1">
          <label className="text-slate-600">Key ID</label>
          <input
            name="key_id"
            value={form.key_id}
            onChange={handleChange}
            className="border border-slate-200 rounded-lg px-2 py-1.5"
            placeholder="Enter Key ID"
          />
          {errors.key_id && <span className="text-red-500 text-xs">{errors.key_id}</span>}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-slate-600">Key Secret</label>
          <input
            name="key_secret"
            value={form.key_secret}
            onChange={handleChange}
            className="border border-slate-200 rounded-lg px-2 py-1.5"
            placeholder="Enter Key Secret"
          />
          {errors.key_secret && (
            <span className="text-red-500 text-xs">{errors.key_secret}</span>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-sm"
        >
          {loading ? "Saving..." : badge ? "Update Key" : "Create Key"}
        </button>
      </form>
    </div>
  );
}



